import React from "react";
import { ArrowUpRight, Mail } from "lucide-react";
import { useContact } from "@/contexts/ContactContext";
import { Reveal } from "@/components/Reveal";

export const FinalCTA = () => {
  const { openContact } = useContact();
  return (
    <section
      id="contact"
      data-testid="final-cta-section"
      className="relative bg-[#000000] border-t border-white/5 overflow-hidden"
    >
      {/* Cinematic backdrop image */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
          alt=""
          className="w-full h-full object-cover opacity-[0.15] animate-kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#000000] via-[#000000]/80 to-[#000000]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(146,24,136,0.18),transparent_65%)]" />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12 lg:px-16 py-16 sm:py-24 md:py-32 lg:py-40">
        <Reveal className="relative max-w-5xl mx-auto border-t border-b border-white/10">
          <CornerTicks />

          <div className="px-5 sm:px-6 md:px-10 py-14 sm:py-16 md:py-20 lg:py-24 text-center">
            <div className="inline-flex items-center justify-center gap-3 mb-8">
              <span className="h-px w-10 bg-fuchsia-500" />
              <span className="text-[10px] uppercase tracking-[0.32em] text-fuchsia-400 font-bold">
                Let&apos;s Build Something
              </span>
              <span className="h-px w-10 bg-fuchsia-500" />
            </div>

            <h2
              data-testid="final-cta-headline"
              className="font-display text-3xl sm:text-4xl md:text-6xl lg:text-[80px] xl:text-[96px] font-black tracking-[-0.04em] leading-[0.95] text-white"
            >
              Ready to transform{" "}
              <span className="text-fuchsia-500 italic font-light">
                your growth?
              </span>
            </h2>

            <p className="mt-8 text-base md:text-lg text-white/65 leading-relaxed max-w-2xl mx-auto font-body">
              Tell us where you want to go. We&apos;ll come back with a tailored
              plan combining strategy, video and partnerships — built around
              your stage, budget and ambition.
            </p>

            <div className="mt-10 md:mt-12 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-4 max-w-md sm:max-w-none mx-auto">
              <button
                type="button"
                onClick={() => openContact({ source: "final-cta" })}
                data-testid="final-cta-primary"
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 ember-glow"
              >
                Schedule a Consultation
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
              </button>
              <a
                href="#services"
                data-testid="final-cta-secondary"
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-3 border border-white/20 hover:border-fuchsia-400 hover:bg-white/5 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300"
              >
                View Our Services
              </a>
            </div>

            <div
              className="mt-12 pt-8 border-t border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center max-w-3xl mx-auto"
              data-testid="final-cta-meta"
            >
              <TrustItem label="Response time" value="Within 24 hours" />
              <TrustItem
                label="Proposal"
                value="Custom, never templated"
                middle
              />
              <TrustItem label="Confidentiality" value="NDA on request" />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const TrustItem = ({ label, value, middle }) => (
  <div
    className={`px-2 ${
      middle ? "sm:border-l sm:border-r border-white/10" : ""
    }`}
  >
    <div className="text-[10px] uppercase tracking-[0.28em] text-fuchsia-400 font-bold mb-1.5">
      {label}
    </div>
    <div className="text-sm md:text-base text-white/80 font-medium">{value}</div>
  </div>
);

const CornerTicks = () => (
  <>
    <span className="absolute -top-px -left-px w-6 h-6 border-t border-l border-fuchsia-500" />
    <span className="absolute -top-px -right-px w-6 h-6 border-t border-r border-fuchsia-500" />
    <span className="absolute -bottom-px -left-px w-6 h-6 border-b border-l border-fuchsia-500" />
    <span className="absolute -bottom-px -right-px w-6 h-6 border-b border-r border-fuchsia-500" />
  </>
);

export const Footer = () => {
  const { openContact } = useContact();
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-black border-t border-white/10 pt-16 md:pt-24 pb-10"
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8">
              <a
                href="#home"
                aria-label="1902 Enterprises"
                className="shrink-0 inline-flex bg-black p-2"
              >
                <img
                  src="/images/logo.png"
                  alt="1902 Enterprises"
                  className="h-24 sm:h-28 md:h-32 w-auto object-contain drop-shadow-[0_8px_32px_rgba(146,24,136,0.4)]"
                />
              </a>
              <div className="min-w-0 flex-1 pt-1">
                <p className="text-base text-white/55 leading-relaxed font-body">
                  Premium marketing & growth partner. We turn visibility into
                  measurable growth through partnerships, video production and
                  performance marketing.
                </p>
                <div className="mt-6 flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => openContact({ source: "footer-email" })}
                    data-testid="footer-social-email"
                    aria-label="Send us a message"
                    className="w-10 h-10 border border-white/15 hover:border-fuchsia-400 hover:bg-fuchsia-500/10 flex items-center justify-center text-white/70 hover:text-white transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-10">
            <FooterCol
              heading="Company"
              links={[
                { l: "About", h: "#about" },
                { l: "Services", h: "#services" },
                { l: "Showcase", h: "#showcase" },
                { l: "How We Work", h: "#how-we-work" },
              ]}
              testid="footer-col-company"
            />
            <FooterCol
              heading="Services"
              links={[
                { l: "Partnerships", h: "#showcase-partnerships" },
                { l: "Video Production", h: "#showcase-video" },
                { l: "Marketing Strategy", h: "#services" },
                { l: "Digital Marketing", h: "#services" },
                { l: "Technology", h: "#technology" },
              ]}
              testid="footer-col-services"
            />
            <FooterCol
              heading="Get in touch"
              links={[
                { l: "Send a message", action: () => openContact({ source: "footer" }) },
                { l: "Schedule a call", action: () => openContact({ source: "footer" }) },
              ]}
              testid="footer-col-contact"
            />
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-white/40 tracking-wide" data-testid="footer-copyright">
            © {new Date().getFullYear()} 1902 Enterprises. All rights reserved.
          </p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">
            Strategy · Story · Scale
          </p>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ heading, links, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-400 font-bold mb-5">
      {heading}
    </div>
    <ul className="space-y-3">
      {links.map((l) =>
        l.action ? (
          <li key={l.l}>
            <button
              type="button"
              onClick={l.action}
              className="text-sm text-white/65 hover:text-fuchsia-400 transition-colors text-left"
            >
              {l.l}
            </button>
          </li>
        ) : (
          <li key={l.l}>
            <a
              href={l.h}
              className="text-sm text-white/65 hover:text-fuchsia-400 transition-colors"
            >
              {l.l}
            </a>
          </li>
        )
      )}
    </ul>
  </div>
);
